import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "paisacx.cms.dismissed";

const TONE = {
  info: "border-primary/40 bg-primary/10 text-foreground",
  success: "border-up/40 bg-up/10 text-foreground",
  warning: "border-warning/40 bg-warning/10 text-foreground",
  critical: "border-down/40 bg-down/10 text-foreground",
};

function readDismissed() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function Cta({ href, children }) {
  if (!href) return null;
  if (href.startsWith("/")) {
    return (
      <Link to={href} className="shrink-0 text-xs font-semibold text-primary hover:underline">
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-semibold text-primary hover:underline">
      {children}
    </a>
  );
}

/** Announcements and banners published from Admin → CMS. */
export default function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [banners, setBanners] = useState([]);
  const [hidden, setHidden] = useState(() => readDismissed());

  useEffect(() => {
    let alive = true;
    api
      .get("/api/cms/landing")
      .then(({ data }) => {
        if (!alive) return;
        setItems(Array.isArray(data?.announcements) ? data.announcements : []);
        setBanners(Array.isArray(data?.banners) ? data.banners : []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  function dismiss(id) {
    const next = new Set(hidden);
    next.add(id);
    setHidden(next);
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...next]));
  }

  const visible = items.filter((a) => a?.id && !hidden.has(a.id));
  const visibleBanners = banners.filter((b) => b?.id && !hidden.has(`banner:${b.id}`));
  if (!visible.length && !visibleBanners.length) return null;

  return (
    <div className="relative z-30 mx-auto w-full max-w-7xl space-y-2 px-4 pt-3" data-testid="cms-banner">
      {visible.map((a) => (
        <div
          key={a.id}
          className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${TONE[a.type] || TONE.info}`}
          data-testid={`announcement-${a.id}`}
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium">{a.title}</p>
            {a.body && <p className="mt-0.5 text-xs text-muted-foreground">{a.body}</p>}
          </div>
          <button
            type="button"
            aria-label="Dismiss announcement"
            onClick={() => dismiss(a.id)}
            className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      {visibleBanners.map((b) => (
        <div
          key={b.id}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface/80 px-3 py-2"
          data-testid={`cms-banner-${b.id}`}
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">{b.title}</p>
            {b.subtitle && <p className="text-xs text-muted-foreground">{b.subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {b.cta_url && b.cta_label && <Cta href={b.cta_url}>{b.cta_label}</Cta>}
            <button
              type="button"
              aria-label="Dismiss banner"
              onClick={() => dismiss(`banner:${b.id}`)}
              className="rounded p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
